<template>
  <div class="pa-6">
    <v-snackbar
      :timeout="3000"
      v-model="showFullAccessCopied"
      color="#00AEEF"
      top
      centered
    >
      <p class="body1 mr-3">
        {{ $t("project_list.modal.share.full_access.snackbar.copy") }}
      </p>
      <v-icon class="snackbar-icon" @click="showFullAccessCopied = false">
        $iconClose
      </v-icon>
    </v-snackbar>
    <template v-for="(email, index) in deletedEmails">
      <v-snackbar
        :key="index"
        :timeout="0"
        class="snackbar"
        v-model="showDeletedSnackbar"
        color="#00AEEF"
        top
        centered
      >
        <p class="body1 mr-3">
          {{
            $t("project_list.modal.share.full_access.snackbar.delete", {
              email,
              project: projectName,
            })
          }}
        </p>
        <v-icon
          class="snackbar__icon"
          @click="
            deletedEmails = deletedEmails.filter((item) => item !== email)
          "
        >
          $iconClose
        </v-icon>
      </v-snackbar>
    </template>
    <h2 class="bold mb-6">
      {{ $t("project_list.modal.share.full_access.title") }}
    </h2>
    <p
      class="body2 mb-6"
      v-html="$t('project_list.modal.share.full_access.description')"
    />
    <v-form
      ref="fullAccessForm"
      v-model="valid"
      @submit.prevent="startShareFullAccess"
      lazy-validation
    >
      <v-subheader class="mb-2 ml-0 pl-0" style="height: auto">
        {{ $t("project_list.modal.share.full_access.email.label") }}
      </v-subheader>
      <div class="d-flex">
        <v-text-field
          v-model="email"
          class="mr-4"
          :placeholder="
            $t('project_list.modal.share.full_access.email.placeholder')
          "
          :rules="[
            error
              ? $t('project_list.modal.share.full_access.email.error')
              : true,
          ]"
          hide-details="auto"
          validate-on-blur
          required
          rounded
          outlined
          filled
        />
        <niko-button
          :text="$t('project_list.modal.share.full_access.grant_access')"
          type="submit"
        />
      </div>
    </v-form>
    <div class="emails mt-6 mb-10">
      <template v-for="(email, index) in selectedProjectEmails">
        <div :key="index" class="emails__row">
          <p class="body2 px-4 py-3">{{ email }}</p>
          <v-btn icon @click.stop="startRevokeAccess(email)">
            <v-icon style="max-height: 20px !important"> $iconDelete </v-icon>
          </v-btn>
        </div>
      </template>
    </div>
    <div class="d-flex align-center">
      <p class="body2">{{ $t("project_list.modal.share.full_access.link") }}</p>
      <v-spacer />
      <niko-button
        :text="$t('project_list.modal.share.full_access.copy_link')"
        @onClick="copyProjectUrl"
        type="button"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import NikoButton from "@/components/UI/NikoButton";
import { emailRegex } from "@/utils/constants";

export default {
  name: "share-full-access",
  components: { NikoButton },
  props: {
    projectName: String,
  },
  data: () => ({
    email: "",
    valid: true,
    showFullAccessCopied: false,
    showDeletedSnackbar: true,
    deletedEmails: [],
  }),
  methods: {
    ...mapActions("projectsStore", ["shareFullAccess", "revokeAccess"]),
    startShareFullAccess() {
      if (this.$refs.fullAccessForm.validate()) {
        this.shareFullAccess(this.emailArray);
        this.email = "";
      }
    },
    copyProjectUrl() {
      const projectUrl = `${window.location.href}project/${this.selectedProjectId}`;
      navigator.clipboard.writeText(projectUrl);
      this.showFullAccessCopied = true;
    },
    startRevokeAccess(email) {
      this.revokeAccess(email);
      this.deletedEmails = [...this.deletedEmails, email];
      setTimeout(() => {
        this.deletedEmails = this.deletedEmails.filter(
          (item) => item !== email
        );
      }, 3000);
    },
  },
  computed: {
    ...mapState("projectsStore", [
      "selectedProjectId",
      "selectedProjectEmails",
    ]),
    emailArray: function () {
      return this.email
        .split(/,|;/)
        .map((item) => item.trim())
        .map((item) => item.toLowerCase())
        .filter((item) => !!item);
    },
    error: function () {
      return !!this.emailArray.find((item) => !emailRegex.test(item));
    },
  },
};
</script>

<style lang="scss">
.emails {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  max-height: 32vh;
  overflow-y: auto;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__row:not(:last-child) {
    border-bottom: 1px solid #4c4c4c;
  }

  &::-webkit-scrollbar {
    background-color: #202324;
    color: #aba499;
    width: 8px;
    border-radius: 100px !important;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #454a4d;
    border-radius: 100px !important;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #575e62;
  }
  &::-webkit-scrollbar-thumb:active {
    background-color: #484e51;
  }
  &::-webkit-scrollbar-corner {
    background-color: #181a1b;
  }
}

@for $i from 2 through 4 {
  .snackbar:nth-child(n + #{$i}) {
    top: calc(55px * #{$i} - 47px);
  }
}

.snackbar {
  &__icon {
    width: 16px !important;
    height: 16px;
  }
}

.v-text-field__details {
  padding-left: 0 !important;
}

.v-input.error--text:before {
  content: "";
  height: calc(100% + 20px);
  width: 4px;
  background: var(--v-error-base);
  position: absolute;
  left: -12px;
  top: -24px;
}
</style>
